import React, { useEffect, useRef, useState, Suspense } from 'react';
import { motion, useInView } from 'framer-motion';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, Cone, Circle } from '@react-three/drei';
import gameDevModel from '../../asset/3D/GameDev.glb';
import kineticInstallationModel from '../../asset/3D/KineticInstallation.glb';
import creativeConceptModel from '../../asset/3D/CreativeConecpt.glb';
import hardwareModel from '../../asset/3D/hardware.glb';
import contentDevelopmentModel from '../../asset/3D/contentDevelopment.glb';
import './Services.css';

// Use placeholder images instead of importing files
const placeholderImage1 = "https://placehold.co/600x400/E32961/FFFFFF?text=Creative+Design";
const placeholderImage2 = "https://placehold.co/600x400/E32961/FFFFFF?text=Web+Development";
const placeholderImage3 = "https://placehold.co/600x400/E32961/FFFFFF?text=Digital+Marketing";



// 3D Model Component for Game Development
const GameDevModel = () => {
  const { scene } = useGLTF(gameDevModel);
  const meshRef = useRef();
  
  // Floating animation - starts immediately
  useFrame((state) => {
    if (meshRef.current) {
      // Force animation to start immediately
      const time = state.clock.elapsedTime;
      meshRef.current.position.y = Math.sin(time) * 0.3;
      meshRef.current.rotation.y = Math.sin(time * 0.5) * 0.1;
    }
  });
  
  // Ensure animation starts immediately on mobile
  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.visible = true;
    }
  }, []);
  
  return <primitive ref={meshRef} object={scene} scale={[3,3,3]} />;
};

// 3D Model Component for Kinetic Installation
const KineticInstallationModel = () => {
  const { scene } = useGLTF(kineticInstallationModel);
  const meshRef = useRef();
  
  // Floating animation - starts immediately
  useFrame((state) => {
    if (meshRef.current) {
      // Force animation to start immediately
      const time = state.clock.elapsedTime;
      meshRef.current.position.y = Math.sin(time) * 0.3;
      meshRef.current.rotation.y = Math.sin(time * 0.5) * 0.1;
    }
  });
  
  // Ensure animation starts immediately on mobile
  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.visible = true;
    }
  }, []);
  
  return <primitive ref={meshRef} object={scene} scale={[2.5, 2.5, 2.5]} />;
};

// 3D Model Component for Creative Concept
const CreativeConceptModel = () => {
  const { scene } = useGLTF(creativeConceptModel);
  const meshRef = useRef();
  
  // Floating animation - starts immediately
  useFrame((state) => {
    if (meshRef.current) {
      // Force animation to start immediately
      const time = state.clock.elapsedTime;
      meshRef.current.position.y = Math.sin(time) * 0.3;
      meshRef.current.rotation.y = Math.sin(time * 0.5) * 0.1;
    }
  });
  
  // Ensure animation starts immediately on mobile
  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.visible = true;
    }
  }, []);
  
  return <primitive ref={meshRef} object={scene} scale={[2.8, 2.8, 2.8]} />;
};

// 3D Model Component for Hardware Development
const HardwareModel = () => {
  const { scene } = useGLTF(hardwareModel);
  const meshRef = useRef();
  
  // Floating animation - starts immediately
  useFrame((state) => {
    if (meshRef.current) {
      // Force animation to start immediately
      const time = state.clock.elapsedTime;
      meshRef.current.position.y = Math.sin(time) * 0.3;
      meshRef.current.rotation.y = Math.sin(time * 0.5) * 0.1;
    }
  });
  
  // Ensure animation starts immediately on mobile
  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.visible = true;
    }
  }, []);
  
  return <primitive ref={meshRef} object={scene} scale={[2.8, 2.8, 2.8]} />;
};

// 3D Model Component for Content Development
const ContentDevelopmentModel = () => {
  const { scene } = useGLTF(contentDevelopmentModel);
  const meshRef = useRef();
  
  // Floating animation - starts immediately
  useFrame((state) => {
    if (meshRef.current) {
      // Force animation to start immediately
      const time = state.clock.elapsedTime;
      meshRef.current.position.y = Math.sin(time) * 0.3;
      meshRef.current.rotation.y = Math.sin(time * 0.5) * 0.1;
    }
  });
  
  // Ensure animation starts immediately on mobile
  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.visible = true;
    }
  }, []);
  
  return <primitive ref={meshRef} object={scene} scale={[2.8, 2.8, 2.8]} />;
};

const ServiceItem = ({ title, description, image, imageOnRight, index, is3D = false, modelType }) => {
  const ref = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const isInView = useInView(ref, { 
    once: true, 
    amount: 0.2
  });
  
  // Improved responsive text truncation
  const [truncatedDescription, setTruncatedDescription] = useState(description);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      
      if (!showFullDescription) {
        // More aggressive truncation for mobile devices
        if (newWidth <= 360) {
          setTruncatedDescription(description.substring(0, 80) + '...');
        } else if (newWidth <= 480) {
          setTruncatedDescription(description.substring(0, 100) + '...');
        } else if (newWidth <= 768) {
          setTruncatedDescription(description.substring(0, 140) + '...');
        } else if (newWidth <= 1024) {
          setTruncatedDescription(description.substring(0, 180) + '...');
        } else {
          setTruncatedDescription(description);
        }
      }
    };

    // Set initial truncated description
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [description, showFullDescription]);

  const handleReadMore = () => {
    setShowFullDescription(true);
    setTruncatedDescription(description);
  };

  const handleReadLess = () => {
    setShowFullDescription(false);
    // Re-trigger truncation
    const newWidth = window.innerWidth;
    if (newWidth <= 360) {
      setTruncatedDescription(description.substring(0, 80) + '...');
    } else if (newWidth <= 480) {
      setTruncatedDescription(description.substring(0, 100) + '...');
    } else if (newWidth <= 768) {
      setTruncatedDescription(description.substring(0, 140) + '...');
    } else if (newWidth <= 1024) {
      setTruncatedDescription(description.substring(0, 180) + '...');
    }
  };

  // Determine if we should show read more button
  const shouldShowReadMore = windowWidth <= 1024 && !showFullDescription && truncatedDescription !== description;
  const shouldShowReadLess = windowWidth <= 1024 && showFullDescription && description.length > 180;

  // Mobile autoplay handling
  useEffect(() => {
    if (is3D && isInView && windowWidth <= 768 && !hasInteracted) {
      // Auto-trigger interaction for mobile to ensure animations start
      const timer = setTimeout(() => {
        setHasInteracted(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [is3D, isInView, windowWidth, hasInteracted]);
  
  return (
    <div 
      className={`service-item ${imageOnRight ? 'image-right' : 'image-left'} ${is3D ? 'has-3d-model' : ''}`} 
      ref={ref}
      id={`service-${index}`}
    >
      <div className="service-container">
        <div 
          className={`service-content ${isInView ? 'animate-in' : ''} ${imageOnRight ? 'slide-from-left' : 'slide-from-right'}`}
        >
          <h3 className={imageOnRight ? 'text-right' : 'text-left'}>{title}</h3>
          <p className={imageOnRight ? 'text-right' : 'text-left'}>{truncatedDescription}</p>
          {shouldShowReadMore && (
            <button 
              className="read-more-btn"
              onClick={handleReadMore}
            >
              Read More
            </button>
          )}
          {shouldShowReadLess && (
            <button 
              className="read-more-btn"
              onClick={handleReadLess}
            >
                           Read Less
             </button>
           )}
         </div>
        
        <div 
          className={`service-image ${isInView ? 'animate-in' : ''} ${imageOnRight ? 'slide-from-right' : 'slide-from-left'}`}
        >
          {is3D ? (
            <div 
              className="model-container"
              onMouseEnter={() => {
                console.log('Hover detected!');
                setIsHovered(true);
              }}
              onMouseLeave={() => {
                console.log('Hover ended!');
                setIsHovered(false);
              }}
              onTouchStart={() => {
                setHasInteracted(true);
                setIsHovered(true);
              }}
              onTouchEnd={() => {
                setIsHovered(false);
              }}
              onClick={() => {
                setHasInteracted(true);
              }}
              style={{ 
                width: '100%', 
                height: windowWidth <= 480 ? '250px' : windowWidth <= 768 ? '300px' : windowWidth <= 1024 ? '350px' : '400px',
                cursor: 'pointer',
                touchAction: 'manipulation'
              }}
            >
              <Canvas 
                camera={{ 
                  position: [-3, -2, 8], 
                  fov: windowWidth <= 480 ? 70 : windowWidth <= 768 ? 65 : 60 
                }}
                style={{ 
                  width: '100%', 
                  height: '100%' 
                }}
                dpr={[1, 2]}
                performance={{ min: 0.5 }}
                gl={{ 
                  antialias: windowWidth > 768,
                  alpha: true,
                  preserveDrawingBuffer: false,
                  powerPreference: "high-performance"
                }}
              >
                {/* Enhanced ambient lighting for visibility */}
                <ambientLight intensity={0.6} color="#ffffff" />
                
                {/* Main directional light - strong and consistent */}
                <directionalLight 
                  position={[10, 10, 5]} 
                  intensity={1.2} 
                  color="#ffffff"
                  castShadow
                />
                
                {/* Secondary directional light for fill */}
                <directionalLight 
                  position={[-5, 8, 3]} 
                  intensity={0.8} 
                  color="#e6f3ff"
                />
                
                {/* Key light from front */}
                <pointLight 
                  position={[0, 5, 8]} 
                  intensity={1.0} 
                  color="#ffffff"
                  distance={25}
                />
                
                {/* Rim light for definition */}
                <pointLight 
                  position={[-8, 3, -5]} 
                  intensity={0.7} 
                  color="#fff5e6"
                  distance={20}
                />
                
                {/* Top-down light */}
                <pointLight 
                  position={[0, 12, 0]} 
                  intensity={0.8} 
                  color="#ffffff"
                  distance={25}
                />
                
                {/* Hemisphere light for natural look */}
                <hemisphereLight
                  skyColor="#87CEEB"
                  groundColor="#8B4513"
                  intensity={0.4}
                />
                
                <Suspense fallback={null}>
                  {modelType === 'gamedev' ? <GameDevModel /> : 
                   modelType === 'kinetic' ? <KineticInstallationModel /> : 
                   modelType === 'hardware' ? <HardwareModel /> :
                   modelType === 'content' ? <ContentDevelopmentModel /> :
                   <CreativeConceptModel />}
                </Suspense>
              </Canvas>
            </div>
                     ) : (
             <img src={image} alt={title} />
           )}
         </div>
      </div>
    </div>
  );
};

const Services = () => {
  const servicesRef = useRef(null);
  

  
  const services = [
    {
      title: "Game Development",
      description: "We create immersive gaming experiences with engaging gameplay mechanics, stunning visuals, and seamless user experiences across multiple platforms. From concept to launch, we handle 3D modeling, animation, physics simulation, and performance optimization.",
      image: null,
      imageOnRight: false,
      is3D: true,
      modelType: 'gamedev'
    },
    {
      title: "Kinetic Installation",
      description: "We design interactive kinetic installations that blend art, technology, and motion. Our installations respond to environmental factors and user interaction, combining sensors, motors, and creative programming to create mesmerizing kinetic sculptures.",
      image: null,
      imageOnRight: true,
      is3D: true,
      modelType: 'kinetic'
    },
    {
      title: "Creative Concept",
      description: "We transform abstract ideas into compelling visual narratives through innovative creative concepts. Our process involves deep collaboration to understand your vision and translate it into unique, memorable experiences that resonate with audiences.",
      image: null,
      imageOnRight: false,
      is3D: true,
      modelType: 'creative'
    },
    {
      title: "Hardware Development",
      description: "We specialize in comprehensive hardware development encompassing mechatronics, mechanical engineering, and electrical systems. We create innovative solutions integrating sensors, actuators, microcontrollers, and custom PCBs for sophisticated mechatronic systems.",
      image: null,
      imageOnRight: true,
      is3D: true,
      modelType: 'hardware'
    },
    {
      title: "Content Development",
      description: "We create compelling digital content across multiple formats and platforms. Our content development services include video production, 3D animations, interactive media, and multimedia presentations that engage audiences and effectively communicate your message.",
      image: null,
      imageOnRight: false,
      is3D: true,
      modelType: 'content'
    }
  ];

  return (
    <section className="services-section" ref={servicesRef}>
      <h2 className="services-heading">
                Our Services
        </h2>
      
      <div className="services-container">
        {services.map((service, index) => (
          <ServiceItem 
            key={`service-${index}`}
            title={service.title}
            description={service.description}
            image={service.image}
            imageOnRight={service.imageOnRight}
            index={index}
            is3D={service.is3D}
            modelType={service.modelType}
          />
        ))}
      </div>
    </section>
  );
};

export default Services; 
